"use client";

import { createContext, type ReactNode, useContext, useEffect, useState } from "react";
import type { RequestInfo } from "shared/RequestInfo";
import { loadShiki } from "src/components/common/Code/ShikiLoader";
import { hydrateDeferredGroup } from "src/components/common/DeferredHydration/DeferredHydrationGates";
import Form from "src/components/form/Form/Form";
import { usePortal } from "src/lib/contexts/PortalContext";
import {
  type DocsLanguage,
  getDocsLanguageSyntax,
  resolveDocsLanguage,
} from "src/lib/DocsLanguage";
import type { RequestPreviewSource } from "src/lib/DocsRequestPreviewCode";
import type { DocsSnippetRequest } from "src/lib/DocsSdkExamples";
import { PUBLIC_URL } from "src/lib/FrontendConstants";
import { useDocsLanguage, useDocsLanguageReader } from "../DocsLanguageContext";
import DocsLanguageTabs, { type DocsTabCode } from "../DocsLanguageTabs";
import MethodUrlDisplay from "../MethodUrlDisplay/MethodUrlDisplay";

/** What an endpoint card's forms and preview need: serializable, so no schemas or error codes. */
export type PlaygroundRequest = DocsSnippetRequest & Pick<RequestInfo, "example">;

type FormValues = Record<string, any>;

type PreviewCode = typeof import("src/lib/DocsRequestPreviewCode");

// The preview starts as the server rendered it; code for edited values is generated in the browser
// by a module that loads once a reader focuses or edits a field.
let previewCode: PreviewCode | undefined;
let previewCodeLoad: Promise<PreviewCode> | undefined;
const loadPreviewCode = () => {
  previewCodeLoad ??= import("src/lib/DocsRequestPreviewCode").then((module) => {
    previewCode = module;
    return module;
  });
  return previewCodeLoad;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** `values` without the empty fields that `previous` doesn't have, at any depth. */
const withoutAddedEmptyFields = (previous: unknown, values: unknown): unknown => {
  if (!isRecord(values)) return values;
  const known = isRecord(previous) ? previous : {};
  return Object.fromEntries(
    Object.entries(values)
      .filter(([key, value]) => key in known || (value !== "" && value !== undefined))
      .map(([key, value]) => [key, withoutAddedEmptyFields(known[key], value)]),
  );
};

const isSameValue = (a: unknown, b: unknown): boolean => {
  if (Array.isArray(a) && Array.isArray(b))
    return a.length === b.length && a.every((item, index) => isSameValue(item, b[index]));
  if (isRecord(a) && isRecord(b)) {
    const keys = Object.keys(a);
    return (
      keys.length === Object.keys(b).length &&
      keys.every((key) => key in b && isSameValue(a[key], b[key]))
    );
  }
  return a === b;
};

interface RequestPlaygroundState {
  request: PlaygroundRequest;
  formValues: FormValues;
  binaryFile?: RequestPreviewSource["binaryFile"];
  tabs: readonly DocsLanguage[];
  /** The values differ from the example the server rendered the preview for. */
  edited: boolean;
  code: PreviewCode | undefined;
  onChange: (values: FormValues) => void;
  onInitialValues: (values: FormValues) => void;
  onFocus: () => void;
}

const RequestPlaygroundContext = createContext<RequestPlaygroundState | null>(null);

const useRequestPlayground = () => {
  const context = useContext(RequestPlaygroundContext);
  if (!context) throw new Error("Request playground parts require RequestPlaygroundProvider");
  return context;
};

export interface RequestPlaygroundProviderProps {
  request: PlaygroundRequest;
  /** The example values the server rendered the preview with. */
  exampleValues: FormValues;
  binaryFile?: RequestPreviewSource["binaryFile"];
  /** The preview's languages, in tab order. */
  tabs: readonly DocsLanguage[];
  /** The DeferredHydration group of the card's forms, which otherwise hydrate when first used. */
  formsGroup: string;
  children: ReactNode;
}

/**
 * The editable state of an endpoint card (`RequestInfoView`): its path, query and body forms feed
 * the request preview. The card itself and its schema trees render on the server; this client
 * island holds only the values and the parts that use them.
 */
export const RequestPlaygroundProvider = (props: RequestPlaygroundProviderProps) => {
  const { request, exampleValues, binaryFile, tabs, formsGroup, children } = props;
  const [formValues, setFormValues] = useState(exampleValues);
  const [code, setCode] = useState(previewCode);
  // Read when a field is focused: subscribing would re-render the card's forms on every language
  // change, and would make React render forms that are still waiting to hydrate afresh.
  const readLanguage = useDocsLanguageReader();
  const edited = !isSameValue(formValues, exampleValues);

  const load = () => {
    if (!code) void loadPreviewCode().then(setCode);
  };
  useEffect(() => {
    if (edited && !code) void loadPreviewCode().then(setCode);
  }, [edited, code]);
  // The forms fill in the visitor's project, and the preview then shows it: hydrate them with the
  // card for visitors who have one.
  const projectId = usePortal()?.selectedProjectId;
  const usesProject = request.path.includes(":projectId");
  useEffect(() => {
    if (projectId && usesProject) hydrateDeferredGroup(formsGroup);
  }, [projectId, usesProject, formsGroup]);

  const state: RequestPlaygroundState = {
    request,
    formValues,
    binaryFile,
    tabs,
    edited,
    code,
    onChange: (values) =>
      setFormValues((previous) => ({
        ...previous,
        ...(withoutAddedEmptyFields({}, values) as FormValues),
      })),
    // The forms report every field once mounted, with "" for those the example leaves out. Leaving
    // those out keeps the preview as the server rendered it until the reader edits a field.
    onInitialValues: (values) =>
      setFormValues((previous) => ({
        ...previous,
        ...(withoutAddedEmptyFields(previous, values) as FormValues),
      })),
    // Edited code is generated and highlighted in the browser: focusing a field loads the
    // generator and the grammar the preview needs.
    onFocus: () => {
      load();
      void loadShiki(getDocsLanguageSyntax(resolveDocsLanguage(readLanguage(), tabs, "request")));
    },
  };
  return <RequestPlaygroundContext value={state}>{children}</RequestPlaygroundContext>;
};

/** The endpoint's method and URL, with its path parameters as fields. */
export const RequestPlaygroundParams = () => {
  const { request, formValues, onChange, onInitialValues, onFocus } = useRequestPlayground();
  // The visitor's project, which the server-rendered (visitor-neutral) card can't know.
  const portal = usePortal();
  return (
    <Form
      defaultValue={{
        params: {
          ...request.example?.params,
          projectId: portal?.selectedProjectId ?? request.example?.params?.projectId,
        },
      }}
      onInitialValues={onInitialValues}
      onFocus={onFocus}
      onChange={(values) => onChange(values)}
    >
      <MethodUrlDisplay
        method={request.method}
        baseUrl={PUBLIC_URL}
        path={request.path}
        name="params"
        defaultValue={formValues.params}
      />
    </Form>
  );
};

/** The form around a server-rendered editable schema tree (query parameters or request body). */
export const RequestPlaygroundForm = (props: { part: "query" | "body"; children: ReactNode }) => {
  const { part, children } = props;
  const { request, onChange, onInitialValues, onFocus } = useRequestPlayground();
  return (
    <Form
      defaultValue={{ [part]: request.example?.[part] }}
      onInitialValues={onInitialValues}
      onFocus={onFocus}
      onChange={(values) => onChange(values)}
    >
      {children}
    </Form>
  );
};

/**
 * The request preview: the example code the server sent (`getRequestPreviewTabs`) until the values
 * change, then code generated from the edited values.
 */
export const RequestPlaygroundPreview = (props: {
  code: Partial<Record<DocsLanguage, DocsTabCode[]>>;
  highlighting?: string;
}) => {
  const { request, formValues, binaryFile, tabs, edited, code } = useRequestPlayground();
  const { language } = useDocsLanguage();
  const selected = resolveDocsLanguage(language, tabs, "request");
  return (
    <DocsLanguageTabs
      tabs={tabs}
      code={
        edited && code
          ? {
              [selected]: [
                code.getRequestPreviewCode({ request, formValues, binaryFile }, selected),
              ],
            }
          : props.code
      }
      highlighting={props.highlighting}
      fallback="request"
      data-docs-request={request.id}
    />
  );
};
